/* eslint-disable @typescript-eslint/no-use-before-define */
import {
  PpvListAction,
  KalturaPpvFilter,
  EntitlementListAction,
  EntitlementCancelRenewalAction,
  SubscriptionListAction,
  KalturaSubscriptionFilter,
  KalturaFilterPager,
  KalturaSubscription,
  KalturaEntitlementFilter,
  ProductPriceListAction,
  KalturaSubscriptionPrice,
  KalturaPpvPrice,
  KalturaProductPriceListResponse,
  SubscriptionSetListAction,
  KalturaSubscriptionDependencySet,
  HouseholdPaymentMethodListAction,
  KalturaProductPrice,
  KalturaEntitlementListResponse,
  KalturaSubscriptionListResponse,
  KalturaEntitlementOrderBy,
  KalturaChannelFilter,
  KalturaAssetMetaOrTagGroupBy,
  KalturaEntitlement,
} from '@kaltura-ott/ott-client-types/api/types';
import { EntitlementListActionArgs } from '@kaltura-ott/ott-client-types/api/types/EntitlementListAction';
import { KalturaEntityReferenceBy } from '@kaltura-ott/ott-client-types/api/types/KalturaEntityReferenceBy';
import {
  KalturaProductPriceFilter,
  KalturaProductPriceFilterArgs,
} from '@kaltura-ott/ott-client-types/api/types/KalturaProductPriceFilter';
import { KalturaTransactionType } from '@kaltura-ott/ott-client-types/api/types/KalturaTransactionType';
import { SubscriptionListActionArgs } from '@kaltura-ott/ott-client-types/api/types/SubscriptionListAction';
import { forkJoin, from, Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { FormatKalturaProductPricePpv, KuxSubscription, KuxPpv, FormatProductPrice, KuxPackage } from '../interfaces';

import { getAssetsByFilter, getAssetsRecursively } from './asset';
import { assetToPpv, assetToSubscription, assetToType } from './asset-v2.helper';
import { getPageNumberArr, sliceArrayOnChunks } from './asset.helper';
import { kalturaClient } from './kaltura-clent';
import { getProductPricesCache, setProductPricesCache } from './subscriptions-cache';
import { getSubscriptionCache, setSubscriptionCache } from './subscriptions-list-cache';

const CHUNK_SIZE = 100;
const PAGE_SIZE = 25;
export const PRODUCT_PRICE_TYPES = {
  subscription: 'subscription',
  file: 'file',
};

interface GetEntitlementsRecursivelyArgs {
  filter: KalturaEntitlementFilter;
  chunkSize?: number;
}

export const getEntitlementsList = (
  entitlementArgs: EntitlementListActionArgs,
): Observable<KalturaEntitlementListResponse> => {
  const client = kalturaClient();
  const action = new EntitlementListAction(entitlementArgs);

  return client.request(action).pipe(
    catchError((error) => {
      console.warn(error);
      return throwError(error);
    }),
  );
};

export interface Ent {
  totalCount: number;
  objects: KalturaEntitlement[];
}

export const getEntitlementsRecursively = ({
  filter,
  chunkSize = PAGE_SIZE,
}: GetEntitlementsRecursivelyArgs): Observable<Ent> => {
  const action = new EntitlementListAction({
    filter,
    pager: new KalturaFilterPager({
      pageSize: chunkSize,
      pageIndex: 1,
    }),
  });
  const client = kalturaClient();

  return client.request(action).pipe(
    switchMap((assets) => {
      const paginatorRequestArr = getPageNumberArr(assets.totalCount, chunkSize, 2).map((pageNumber) =>
        getEntitlementsList({
          filter,
          pager: new KalturaFilterPager({
            pageIndex: pageNumber,
            pageSize: chunkSize,
          }),
        }),
      );
      return forkJoin([of(assets), ...paginatorRequestArr]);
    }),
    map((result) => {
      return {
        ...result[0],
        totalCount: result[0].totalCount,
        objects: result.reduce((prev: KalturaEntitlement[], cur) => {
          return [...prev, ...cur.objects];
        }, []),
      };
    }),
  );
};

/**
 * Build request payload for service 'Entitlement' action 'List'.
 *
 * Usage: get user entitlements
 *
 * @category Purchase
 * @function
 */

export const subscriptionsGetEntitlements = () => {
  const filter = new KalturaEntitlementFilter({
    productTypeEqual: KalturaTransactionType.subscription,
    entityReferenceEqual: KalturaEntityReferenceBy.household,
    isExpiredEqual: false,
  });
  return getEntitlementsRecursively({ filter });
};

export const subscriptionsGetPpvEntitlements = () => {
  const filter = new KalturaEntitlementFilter({
    productTypeEqual: KalturaTransactionType.ppv,
    isExpiredEqual: false,
    orderBy: KalturaEntitlementOrderBy.purchaseDateDesc,
  });
  return getEntitlementsRecursively({ filter });
};

export const getSubscriptions = (args: SubscriptionListActionArgs): Observable<KalturaSubscriptionListResponse> => {
  const action = new SubscriptionListAction(args);
  const client = kalturaClient();

  return client.request(action).pipe(
    catchError((error) => {
      console.warn(error);
      return throwError(error);
    }),
  );
};

/**
 * Build request payload for service 'Subscription' action 'List'.
 *
 * Usage: get user subscriptions
 *
 * @category Purchase
 * @function
 */

export const getSubscriptionsList = (subscriptionIds: string[], mediaFileId = 0): Observable<KuxSubscription[]> => {
  const client = kalturaClient();
  const filter = mediaFileId
    ? new KalturaSubscriptionFilter({ mediaFileIdEqual: mediaFileId })
    : new KalturaSubscriptionFilter({ subscriptionIdIn: subscriptionIds.join(', ') });
  const action = new SubscriptionListAction({
    filter,
    pager: new KalturaFilterPager({
      pageSize: PAGE_SIZE,
      pageIndex: 1,
    }),
  });
  const cacheKey = mediaFileId ? { mediaFileId } : { subscriptionIds };

  return from(getSubscriptionCache(cacheKey)).pipe(
    switchMap((cachedSubscriptions) => {
      if (cachedSubscriptions && cachedSubscriptions?.length !== 0) {
        return of(cachedSubscriptions.map((subscription) => assetToSubscription(subscription as KalturaSubscription)));
      }

      return client.request(action).pipe(
        switchMap((assets) => {
          const paginatorRequestArr = getPageNumberArr(assets.totalCount, PAGE_SIZE, 2).map((pageNumber) =>
            getSubscriptions({
              filter,
              pager: new KalturaFilterPager({
                pageIndex: pageNumber,
                pageSize: PAGE_SIZE,
              }),
            }),
          );
          return forkJoin([of(assets), ...paginatorRequestArr]);
        }),
        map((result: KalturaSubscriptionListResponse[]) => {
          const subscriptions: KalturaSubscription[] = result.reduce(
            (prev: KalturaSubscription[], cur) => [...prev, ...cur.objects],
            [],
          );
          setSubscriptionCache(cacheKey, subscriptions);
          return subscriptions.map((subscription: KalturaSubscription) => assetToSubscription(subscription));
        }),
      );
    }),
  );
};

/**
 * Build request payload for service 'Subscription' action 'List'.
 *
 * Usage: get user subscriptions for settings
 *
 * @category Purchase
 * @function
 */
export const subscriptionsGetList = (subscriptionIds: string[]): Observable<KuxSubscription[]> => {
  return getSubscriptionsList(subscriptionIds, 0);
};

/**
 * Build request payload for service 'Subscription' action 'List'.
 *
 * Usage: get corresponded subscription for some media file
 *
 * @category Purchase
 * @function
 */
export const mediaFileGetSubscription = (mediaFileId: number) => {
  return getSubscriptionsList([], mediaFileId);
};

/**
 * Get subscription assets
 * Usage: get user assets from subscriptions
 *
 * @category Purchase
 * @function
 */
export const subscriptionsGetAssets = (
  subscriptionIds: string[],
  assetType?: string,
  orderBy = 'START_DATE_ASC',
): Observable<KuxPackage[]> => {
  return getAssetsByFilter({
    ...(orderBy ? { orderBy: orderBy } : {}),
    kSql: `(and (or asset_type = '${assetType}' ) (or ${subscriptionIds
      .map((id) => `SubscriptionID = '${id}'`)
      .join(' ')} ) )`,
  }).pipe(
    map((assetList) => {
      const assets = assetList.objects;
      return assets.map((asset) => assetToType(asset)).filter(Boolean) as KuxPackage[];
    }),
  );
};

/**
 * Get subscription sets
 * Usage: get subscription sets
 *
 * @category Purchase
 * @function
 */
export const subscriptionsGetSets = () => {
  const client = kalturaClient();
  const action = new SubscriptionSetListAction();
  return client.request(action).pipe(
    map((packageList) => {
      return (packageList?.objects as KalturaSubscriptionDependencySet[]) || [];
    }),
  );
};

/**
 * Forms appropriate kSql for collectionGetAssetList
 *
 * @param assetType
 * @param basePackageIds
 * @param subscriptionExcludeIds
 * @param userCustomerType
 * @category Purchase
 * @function
 */
export const prepareCollectionGetAssetListKSql = ({
  assetType,
  packageSubscriptionIds,
  subscriptionExcludeIds,
  userCustomerType,
}: PrepareCollectionGetAssetListArgs) => {
  let assetFilter = '';
  let packageSubscriptionsFilter = '';
  let subscriptionFilter = '';
  let customerTypeBlacklistFilter = '';

  if (assetType) {
    assetFilter = `(or asset_type='${assetType}')`;
  }
  if (packageSubscriptionIds.length > 0) {
    packageSubscriptionsFilter = `(or ${packageSubscriptionIds.map((id) => `SubscriptionID='${id}'`).join(' ')})`;
  }
  if (subscriptionExcludeIds.length > 0) {
    subscriptionFilter = `(and ${subscriptionExcludeIds.map((id) => `SubscriptionID!='${id}'`).join(' ')})`;
  }
  if (userCustomerType) {
    customerTypeBlacklistFilter = ` CustomerTypeBlacklist!='${userCustomerType}'`;
  }

  return {
    assetFilter,
    packageSubscriptionsFilter,
    subscriptionFilter,
    customerTypeBlacklistFilter,
  };
};

interface CollectionGetAssetListArgs {
  assetType: string;
  collectionId: number;
  packageSubscriptionIds: string[];
  subscriptionExcludeIds: string[];
  userCustomerType: string;
}

interface PrepareCollectionGetAssetListArgs {
  assetType: string;
  packageSubscriptionIds: string[];
  subscriptionExcludeIds: string[];
  userCustomerType: string;
}

/**
 * Get subscription asset list for “Some offers for you” and “Hero banner”
 * Usage: get user subscription asset list
 *
 * @param assetType
 * @param collectionId
 * @param basePackageIds
 * @param subscriptionExcludeIds
 * @param userCustomerType
 * @category Purchase
 * @function
 */
export const collectionGetAssetList = ({
  assetType,
  collectionId,
  packageSubscriptionIds,
  subscriptionExcludeIds,
  userCustomerType,
}: CollectionGetAssetListArgs): Observable<KuxPackage[]> => {
  const { assetFilter, packageSubscriptionsFilter, subscriptionFilter, customerTypeBlacklistFilter } =
    prepareCollectionGetAssetListKSql({
      assetType,
      packageSubscriptionIds,
      subscriptionExcludeIds,
      userCustomerType,
    });

  const groupBy = '';
  const kSql = `(and ${assetFilter} ${packageSubscriptionsFilter} ${subscriptionFilter} ${customerTypeBlacklistFilter})`;

  const filter = new KalturaChannelFilter({
    idEqual: collectionId,
    kSql,
    ...(groupBy ? { groupBy: [new KalturaAssetMetaOrTagGroupBy({ value: groupBy })] } : {}),
  });

  return getAssetsRecursively({
    filter,
  }).pipe(
    map((assetList) => {
      if (!assetList || assetList?.objects?.length === 0) return [];
      const assets = assetList.objects;
      return assets.map((asset) => assetToType(asset)).filter(Boolean) as KuxPackage[];
    }),
  );
};

/**
 * Get subscription asset list for “Base subscriptions”
 * Usage: get user subscription asset list
 *
 * @param collectionId
 * @param  customerType
 * @category Purchase
 * @function
 */
export const collectionGetBaseAssetList = (collectionId: number, customerType: string): Observable<KuxPackage[]> => {
  const filter = new KalturaChannelFilter({
    idEqual: collectionId,
    kSql: `(or (and planToUserTypes = '${customerType}') (and planToUserTypes = 'all'))`,
  });

  return getAssetsRecursively({
    filter,
  }).pipe(
    map((assetList) => {
      if (!assetList || assetList?.objects?.length === 0) return [];
      const assets = assetList.objects;
      return assets.map((asset) => assetToType(asset)).filter(Boolean) as KuxPackage[];
    }),
  );
};

/**
 * Format asset to corresponded view: from KalturaProductPrice to viewable KalturaSubscriptionPrice
 *
 * Usage: subscriptionsGetProductPrices - format product price to subscription price
 *
 * @category Purchase
 * @function
 */

export const formatKalturaProductPrice = (productPrice: KalturaSubscriptionPrice): FormatProductPrice => {
  return {
    productId: productPrice.productId,
    purchaseStatus: productPrice.purchaseStatus,
    productType: productPrice.productType,
    endDate: productPrice.endDate,
    price: {
      amount: productPrice.price.amount,
      currency: productPrice.price.currency,
      currencySign: productPrice.price.currencySign,
    },
  };
};

/**
 * Format asset to corresponded view: from KalturaProductPrice to viewable KalturaPpvPrice
 *
 * Usage: filesGetProductPrices - format product price to channel price
 *
 * @category Purchase
 * @function
 */
export const formatKalturaProductPricePpv = (productPrice: KalturaPpvPrice): FormatKalturaProductPricePpv => {
  return {
    productId: productPrice.productId,
    fileId: productPrice.fileId,
    purchaseStatus: productPrice.purchaseStatus,
    productType: productPrice.productType,
    startDate: productPrice.startDate,
    endDate: productPrice.endDate,
    isInCancelationPeriod: productPrice.isInCancelationPeriod,
    isSubscriptionOnly: productPrice.isSubscriptionOnly,
    ppvModuleId: productPrice.ppvModuleId,
    price: {
      amount: productPrice?.price?.amount,
      currency: productPrice?.price?.currency,
      currencySign: productPrice?.price?.currencySign,
    },
  };
};

/**
 * Get subscriptions prices
 *
 * @category Subscriptions
 * @function
 */
export const subscriptionsGetProductPrices = (subscriptionIds: number[]): Observable<FormatProductPrice[]> => {
  return getProductPrices(PRODUCT_PRICE_TYPES.subscription, subscriptionIds).pipe(
    map((productPrices) => {
      if (!productPrices || !productPrices.length) return [];
      return productPrices.map((productPrice) => formatKalturaProductPrice(productPrice as KalturaSubscriptionPrice));
    }),
  );
};

/**
 *
 * TODO check withCache params
 *
 * Get files prices
 *
 * @category Purchase
 * @function
 */
export const filesGetProductPrices = (
  fileIds: number[],
  withCache = false,
): Observable<FormatKalturaProductPricePpv[]> => {
  const { cachedFilePrices, noCachedFilePriceIds } = getProductPricesCache({ fileIds });

  if (noCachedFilePriceIds.length === 0) {
    return of(cachedFilePrices);
  }

  return getProductPrices(PRODUCT_PRICE_TYPES.file, noCachedFilePriceIds).pipe(
    map((productPrices) => {
      if (!productPrices || !productPrices.length) return [];
      // @ts-expect-error TODO Fix
      const formatedProductPrices = productPrices.map((price: KalturaPpvPrice) => formatKalturaProductPricePpv(price));
      setProductPricesCache(formatedProductPrices);
      return [...cachedFilePrices, ...formatedProductPrices];
    }),
  );
};

export const getProductList = (
  getProductListArgs: KalturaProductPriceFilterArgs,
): Observable<KalturaProductPriceListResponse> => {
  const client = kalturaClient();
  const action = new ProductPriceListAction({
    filter: new KalturaProductPriceFilter(getProductListArgs),
  });
  return client.request(action);
};

/**
 * Build request payload for service 'ProductPrice' action 'List'.
 *
 * Usage: get product prices
 *
 * @category Purchase
 * @function
 */
export const getProductPrices = (type = 'subscription', ids: number[] = []): Observable<KalturaProductPrice[]> => {
  const chunkedArray = sliceArrayOnChunks(ids, CHUNK_SIZE);
  return forkJoin(
    chunkedArray.map((idsChunk) =>
      getProductList({
        subscriptionIdIn: type === PRODUCT_PRICE_TYPES.subscription ? idsChunk.join(',') : '',
        fileIdIn: type === PRODUCT_PRICE_TYPES.file ? idsChunk.join(',') : '',
        collectionIdIn: '',
        isLowest: false,
        couponCodeEqual: '',
      }),
    ),
  ).pipe(
    map((result) =>
      result.reduce((prev: KalturaProductPrice[], cur) => [...prev, ...cur.objects], [] as KalturaProductPrice[]),
    ),
  );
};

/**
 * Build request payload for service 'Entitlement' action 'Cancel'.
 *
 * Usage: cancel subscription
 *
 * @param subscriptionId number
 * @category Purchase
 * @function
 */
export const subscriptionsCancel = (subscriptionId: string) => {
  const client = kalturaClient();
  const action = new EntitlementCancelRenewalAction({
    subscriptionId: `${subscriptionId}`,
  });
  return client.request(action).pipe(
    catchError((error) => {
      console.warn(error);
      return throwError(error);
    }),
  );
};

export const getHouseholdPaymentMethodList = () => {
  const client = kalturaClient();
  const action = new HouseholdPaymentMethodListAction({});

  return client.request(action).pipe(
    catchError((error) => {
      return throwError(error);
    }),
  );
};

export const getPpvList = (
  ppvModuleIds: string[],
  productPrices: KalturaPpvPrice[],
): Observable<(KuxPpv | undefined)[]> => {
  const client = kalturaClient();
  const ids = ppvModuleIds.join(', ');
  const filter = new KalturaPpvFilter({ idIn: ids });
  const action = new PpvListAction({
    filter,
  });
  const productPriceMap = new Map();
  productPrices.forEach((productPrice) => {
    productPriceMap.set(productPrice.ppvModuleId, productPrice.price);
  });

  return client.request(action).pipe(
    map(
      (response) =>
        response?.objects.map((ppv) => {
          if (ppv) {
            const price = productPriceMap.get(ppv.id);
            return assetToPpv(ppv, price);
          }
        }),
    ),
    catchError((error) => {
      return throwError(error);
    }),
  );
};

export const getPpvByFileIds = (fileId: number): Observable<(KuxPpv | undefined)[]> => {
  const fileIds = [fileId];
  return getProductPrices(PRODUCT_PRICE_TYPES.file, fileIds).pipe(
    // @ts-expect-error TODO Fix
    switchMap((productPrices: KalturaPpvPrice[]) => {
      const ppvModuleIds = productPrices.map((productPrice) => productPrice.ppvModuleId).filter(Boolean);

      return ppvModuleIds.length ? getPpvList(ppvModuleIds, productPrices) : of([]);
    }),
  );
};
